import type { MeasurementKey, RawReadingValue } from './types';
import { MEASUREMENT_FIELDS } from './types';

export interface Warning {
  key: MeasurementKey;
  severity: 'warn' | 'info';
  message: string;
}

/**
 * Range checks. These WARN, they never block — the spec is explicit that an
 * insistent user overrides us. A real measurement that looks odd is still a
 * real measurement; our job is to make them look twice.
 */
export function warningsFor(key: MeasurementKey, v: RawReadingValue | null): Warning[] {
  if (!v || v.value === null || v.value === undefined || Number.isNaN(v.value)) return [];
  const spec = MEASUREMENT_FIELDS[key];
  const out: Warning[] = [];
  const { min, max } = spec.sane;

  if (v.value < min || v.value > max) {
    out.push({
      key,
      severity: 'warn',
      message:
        key === 'ee'
          ? `${v.value}% is outside 0–100% — almost certainly a misread.`
          : `${v.value}${spec.unit ? ' ' + spec.unit : ''} is outside the expected ${min}–${max}${spec.unit ? ' ' + spec.unit : ''} range.`,
    });
  }

  if (v.sd !== null && v.sd !== undefined && !Number.isNaN(v.sd)) {
    if (v.sd < 0) {
      out.push({ key, severity: 'warn', message: 'Standard deviation cannot be negative.' });
    } else if (v.value !== 0 && Math.abs(v.sd) > Math.abs(v.value)) {
      out.push({
        key,
        severity: 'info',
        message: 'SD is larger than the value itself — check the SD column was read correctly.',
      });
    }
  }

  if (key === 'zeta' && v.value > 0) {
    out.push({
      key,
      severity: 'info',
      message: 'Positive zeta. Confirm the sign is right — a dropped minus reads as plausible.',
    });
  }

  return out;
}

export function hasValue(v: RawReadingValue | null | undefined): boolean {
  return !!v && v.value !== null && v.value !== undefined && !Number.isNaN(v.value);
}
